import { formatBrl } from '@estac1/shared';
import { MetricCard } from './metric-card.js';
import type { MetricsResponse } from './types.js';
import { formatHours, formatMonthRange, formatPercent } from './utils.js';

interface RevenueSectionProps {
  m: MetricsResponse;
}

export const RevenueSection = ({ m }: RevenueSectionProps) => (
  <section className="space-y-3">
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
      <h2 className="text-sm font-semibold">Receita recebida</h2>
      <span className="text-xs text-muted-fg">{formatMonthRange(m.window.from, m.window.to)}</span>
    </div>
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <MetricCard
        label="Recebido (bruto)"
        value={formatBrl(m.revenue.grossPaidCents)}
        hint={`${m.revenue.paidInvoices} fatura(s) paga(s)`}
      />
      <MetricCard
        label="Recebido (líquido)"
        value={formatBrl(m.revenue.netPaidCents)}
        hint="após taxas do provedor"
        tone="success"
      />
      <MetricCard
        label="Faturas atrasadas"
        value={m.revenue.overdueInvoices.toString()}
        tone={m.revenue.overdueInvoices > 0 ? 'danger' : undefined}
      />
    </div>
  </section>
);

interface SmsSectionProps {
  m: MetricsResponse;
}

export const SmsSection = ({ m }: SmsSectionProps) => (
  <section className="space-y-3">
    <h2 className="text-sm font-semibold">SMS (no período)</h2>
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <MetricCard
        label="Taxa de entrega"
        value={formatPercent(m.sms.deliveryRatePercent)}
        hint={`${m.sms.delivered} de ${m.sms.total} entregues`}
        tone={
          m.sms.deliveryRatePercent != null && m.sms.deliveryRatePercent < 90 ? 'danger' : undefined
        }
      />
      <MetricCard label="Enviados" value={m.sms.total.toString()} />
      <MetricCard
        label="Falhas"
        value={m.sms.failed.toString()}
        tone={m.sms.failed > 0 ? 'danger' : undefined}
      />
    </div>
  </section>
);

interface ProductSectionProps {
  m: MetricsResponse;
}

export const ProductSection = ({ m }: ProductSectionProps) => (
  <section className="space-y-3">
    <h2 className="text-sm font-semibold">Produto (key results)</h2>
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      <MetricCard
        label="Acerto do OCR"
        value={formatPercent(m.product.ocr.accuracyPercent)}
        hint={
          m.product.ocr.withSuggestion > 0
            ? `${m.product.ocr.correct} de ${m.product.ocr.withSuggestion} sem edição (meta ≥ 85%)`
            : 'sem leituras no período'
        }
        tone={
          m.product.ocr.accuracyPercent != null && m.product.ocr.accuracyPercent >= 85
            ? 'success'
            : undefined
        }
      />
      <MetricCard
        label="Abertura do link SMS"
        value={formatPercent(m.product.smsOpen.openRatePercent)}
        hint={
          m.product.smsOpen.delivered > 0
            ? `${m.product.smsOpen.opened} de ${m.product.smsOpen.delivered} entregues (meta ≥ 50%)`
            : 'sem SMS entregues no período'
        }
        tone={
          m.product.smsOpen.openRatePercent != null && m.product.smsOpen.openRatePercent >= 50
            ? 'success'
            : undefined
        }
      />
      <MetricCard
        label="Tempo até 1º ticket"
        value={
          m.product.timeToFirstTicket.medianHours != null
            ? formatHours(m.product.timeToFirstTicket.medianHours)
            : '—'
        }
        hint={
          m.product.timeToFirstTicket.ownersWithFirstTicket > 0
            ? `mediana de ${m.product.timeToFirstTicket.ownersWithFirstTicket} dono(s) (meta ≤ 24h)`
            : 'nenhum dono fechou ticket'
        }
        tone={
          m.product.timeToFirstTicket.medianHours != null &&
          m.product.timeToFirstTicket.medianHours <= 24
            ? 'success'
            : undefined
        }
      />
    </div>
  </section>
);
